import React, { ReactNode } from 'react';
import { ThemeProvider } from 'styled-components';
import { TEMPLATE_THEMES } from '../Theme';
import { PageHead } from '../Layout';
import { ContentfulAsset } from '../Contentful';
import { RequireFeatureFlag } from '../Auth';
import { LandingPageTemplate } from './LandingPageTemplate';
import { ArticleTemplate } from './ArticleTemplate';
import { getTemplateSocialCardUrl } from './socialCard';
import { HelpHomeTemplate } from './Help/HelpHomeTemplate';

export { templateLoader, templateUrlsLoader } from './useTemplate';
export { LandingPageTemplate } from './LandingPageTemplate';

export type TemplateContentType = 'landingPage' | 'videoPage' | 'articlePage';

// base fields supported by all template types
export type TemplateType = {
  contentType: TemplateContentType;
  id: string;
  title?: string;
  description?: string;
  urlSlug?: string;
  theme?: string;
  requiredFeatureFlag?: string;
  socialCard?: ContentfulAsset | string;
  canonicalUrl?: string;
  releaseDate?: Date;
  introduction?: string;
  article?: string;
  preview?: boolean;
};

type TemplateParams = TemplateType & {
  children?: ReactNode;
};

export const TEMPLATE_COMPONENTS: { [key: string]: React.FC<TemplateType> } = {
  landingPage: LandingPageTemplate,
  videoPage: ArticleTemplate,
  articlePage: ArticleTemplate,
  helpHomePage: HelpHomeTemplate,
};

export const Template: React.FC<TemplateParams> = ({
  children,
  ...template
}) => {
  const {
    contentType,
    title = '',
    description = '',
    theme = 'Dark',
    requiredFeatureFlag,
    canonicalUrl,
    preview = false,
  } = template;

  const TemplateComponent = TEMPLATE_COMPONENTS[contentType];

  if (!TemplateComponent) {
    return <span>Template type "{contentType}" not found</span>;
  }

  const tplTheme = TEMPLATE_THEMES[theme];

  const templateContent = (
    <ThemeProvider theme={tplTheme}>
      <PageHead
        title={title}
        description={description}
        image={getTemplateSocialCardUrl(template)}
        canonicalUrl={canonicalUrl}
      />
      <TemplateComponent {...template}>{children}</TemplateComponent>
    </ThemeProvider>
  );

  if (
    requiredFeatureFlag &&
    process.env.NODE_ENV !== 'development' &&
    !preview
  ) {
    return (
      <RequireFeatureFlag flag={requiredFeatureFlag}>
        {templateContent}
      </RequireFeatureFlag>
    );
  }

  return <>{templateContent}</>;
};
